import React, { useState } from "react";
import {
  Table,
  Row,
  Col,
  Image,
  Spinner,
} from "react-bootstrap";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  CartesianGrid,
  Legend as RechartsLegend,
  Label,
} from "recharts";
import { FaUserCircle, FaStar, FaSearch } from "react-icons/fa"; // Default user icon
import CountryList from "../CountryList";
import { appleApi } from "../../services/api";

const ReviewAndRatingManagement = () => {
  const [formInputs, setFormInputs] = useState({
    appName: "",
    appId: "",
    country: "",
  });
  const [reviews, setReviews] = useState([]);
  const [, setError] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [sentimentData, setSentimentData] = useState([]);
  const [loading, setLoading] = useState(false); // New loading state
  const [, setCountryCode] = useState("");

  const handleInputChange = ({ target: { name, value } }) => {
    setFormInputs({ ...formInputs, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { appName, appId, country } = formInputs;

    setLoading(true); // Start loading

    try {
      const { data: result } = await appleApi.get("get_reviews/", {
        params: {
          country,
          app_name: appName,
          app_id: appId,
        },
      });

      const reviews = result?.reviews ?? [];
      const suggestions = result?.suggestions ?? [];
      const sentimentCounts = (result?.sentiment_analysis ?? []).reduce((acc, item) => {
        const key = item?.sentiment ?? "Unknown";
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});

      setReviews(reviews);
      setSuggestions(suggestions);
      setSentimentData(
        Object.entries(sentimentCounts).map(([sentiment, count]) => ({ sentiment, count }))
      );
    } catch {
      setError("Something went wrong. Please check your connection.");
    } finally {
      setLoading(false); // Stop loading
    }
  };

  const renderStars = (score) =>
    [...Array(5)].map((_, i) => (
      <span key={i} className={`star ${i < score ? "filled" : "empty"}`}>
        ★
      </span>
    ));

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header Section */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-8 border border-gray-100">
        <div className="flex items-center mb-6">
          <FaStar className="text-yellow-500 text-2xl mr-3" />
          <h2 className="text-2xl font-bold text-gray-800">Reviews & Ratings</h2>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <div className="md:col-span-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                App Name
              </label>
              <input
                type="text"
                name="appName"
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                value={formInputs.appName}
                placeholder="Enter App Name"
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                App ID
              </label>
              <input
                type="text"
                name="appId"
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                value={formInputs.appId}
                placeholder="Enter App ID"
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Country
              </label>
              <CountryList
                onSelect={(selectedCountry) => {
                  setCountryCode(selectedCountry);
                  handleInputChange({
                    target: { name: "country", value: selectedCountry },
                  });
                }}
              />
            </div>

            <div className="md:col-span-2 flex items-end">
              <button
                type="submit"
                className="w-full bg-blue-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
              >
                <FaSearch className="mr-2" /> Analyze
              </button>
            </div>
          </div>
        </form>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Spinner animation="border" role="status" className="text-blue-600" />
          <span className="ml-3 text-lg text-gray-600 font-medium">Loading... Please Wait</span>
        </div>
      ) : (
        <>
          <Row>
            <Col md={6} className="mb-3">
              <div
                className="bg-white rounded-xl shadow-md p-4 h-full border border-gray-100"
              >
                <LineChart
                  width={450}
                  height={250}
                  data={sentimentData}
                  margin={{ top: 20, right: 20, bottom: 40, left: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="sentiment">
                    <Label value="Sentiment Type" position="insideBottom" />
                  </XAxis>
                  <YAxis>
                    <Label
                      value="Count"
                      angle={-90}
                      position="insideLeft"
                      style={{ textAnchor: "middle" }}
                    />
                  </YAxis>
                  <RechartsTooltip />
                  <RechartsLegend />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#8884d8"
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              </div>
            </Col>

            <Col md={6}>
              <div className="bg-white rounded-xl shadow-md p-4 h-full border border-gray-100 max-h-[300px] overflow-y-auto">
                <Table
                  striped
                  bordered
                  hover
                  responsive
                  className="mb-0"
                >
                  <thead className="bg-blue-50">
                    <tr>
                      <th className="text-blue-900 font-semibold">#</th>
                      <th className="text-blue-900 font-semibold">Suggestion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suggestions.length ? (
                      suggestions.map((suggestion, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{suggestion}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="2" className="text-center text-gray-500">
                          No suggestions available.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Col>
          </Row>

          <div className="mt-6 bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
            <Table
              striped
              bordered
              hover
              responsive
              className="mb-0"
            >
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">User Photo</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">User Name</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Review</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Sentiment</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Stars</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Score</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reviews.length ? (
                  reviews.map(
                    (
                      { userImage, userName, content, sentiment, score },
                      index
                    ) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {userImage ? (
                            <Image
                              src={userImage}
                              roundedCircle
                              width={30}
                              height={30}
                            />
                          ) : (
                            <FaUserCircle size={30} color="gray" />
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">{userName}</td>
                        <td className="px-6 py-4 text-gray-500 min-w-[300px]">{content}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            sentiment === 'Positive' ? 'bg-green-100 text-green-800' :
                            sentiment === 'Negative' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {sentiment}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{renderStars(score)}</td>
                        <td className="px-6 py-4 whitespace-nowrap font-semibold">{score}/5</td>
                      </tr>
                    )
                  )
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-8 text-gray-500">
                      No reviews available.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </>
      )}
      <style jsx>{`
        .star {
          color: #ddd;
          font-size: 1.2em;
        }
        .filled {
          color: #fbbf24;
        }
        .empty {
          color: #d1d5db;
        }
      `}</style>
    </div>
  );
};

export default ReviewAndRatingManagement;
